import { Box, Popover, Tooltip, Typography } from "@mui/material";
import { useState, type ReactNode } from "react";

/**
 * The rail is sized around its item, not the other way round: 48px target with
 * a 14px gutter either side. At 100px the icons floated in the middle of an
 * empty column and the active edge-bar sat 26px away from the thing it marks.
 */
export const RAIL_ITEM = 48;
export const RAIL_WIDTH = RAIL_ITEM + 28;
const ITEM = RAIL_ITEM;

/**
 * One icon in the collapsed rail.
 *
 * The active item is marked twice, as in the design: a tinted rounded square,
 * plus a 4px bar on the rail's outer edge. The bar is what you actually see
 * from across the room - the tint alone is easy to miss at this size.
 */
export function RailItem({
  icon,
  label,
  active,
  onClick,
  testId,
  danger,
}: {
  icon: ReactNode;
  label: string;
  active?: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  testId?: string;
  danger?: boolean;
}) {
  return (
    <Tooltip title={label} placement="right">
      <Box
        role="button"
        tabIndex={0}
        aria-label={label}
        data-testid={testId}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick(e as unknown as React.MouseEvent<HTMLElement>);
          }
        }}
        sx={{
          position: "relative",
          width: ITEM,
          height: ITEM,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 2,
          cursor: "pointer",
          color: danger
            ? "error.main"
            : active
              ? "primary.main"
              : "text.primary",
          bgcolor: active ? "action.selected" : "transparent",
          "&:hover": { bgcolor: active ? "action.selected" : "action.hover" },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 2,
          },
          "& svg": { fontSize: 22 },
        }}
      >
        {icon}
        {active && (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              left: `-${(RAIL_WIDTH - ITEM) / 2}px`,
              top: "50%",
              transform: "translateY(-50%)",
              width: 4,
              height: 38,
              borderRadius: "0 12px 12px 0",
              bgcolor: "primary.main",
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
}

export interface RailLink {
  label: string;
  href: string;
  testId: string;
  active?: boolean;
  disabled?: boolean;
}

/**
 * A group in the collapsed rail: one icon, and its sections in a flyout card
 * anchored beside it.
 *
 * The rail deliberately does NOT flatten a group into its children (which is
 * what it used to do) - eight unlabelled icons in a column tell you nothing
 * about which product they belong to.
 */
export function RailGroup({
  icon,
  label,
  links,
  active,
  onNavigate,
  testId,
  onSelfClick,
}: {
  icon: ReactNode;
  label: string;
  links: RailLink[];
  active?: boolean;
  onNavigate: (href: string) => void;
  testId?: string;
  /** Optional: the group itself is also a destination (its list page). */
  onSelfClick?: () => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <RailItem
        icon={icon}
        label={label}
        active={active}
        testId={testId}
        onClick={(e) => {
          if (links.length === 0) {
            onSelfClick?.();
            return;
          }
          setAnchor(e.currentTarget);
        }}
      />
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              ml: 1.5,
              width: 200,
              p: 1,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              boxShadow:
                "0 24px 48px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06)",
            },
          },
        }}
      >
        <Typography
          variant="overline"
          sx={{ display: "block", px: 1.5, pt: 0.5, color: "text.secondary" }}
        >
          {label}
        </Typography>
        {onSelfClick && (
          <FlyoutLink
            label={`All ${label.toLowerCase()}`}
            onClick={() => {
              onSelfClick();
              setAnchor(null);
            }}
          />
        )}
        {links.map((l) => (
          <FlyoutLink
            key={l.testId}
            label={l.label}
            active={l.active}
            disabled={l.disabled}
            testId={l.testId}
            onClick={() => {
              onNavigate(l.href);
              setAnchor(null);
            }}
          />
        ))}
      </Popover>
    </>
  );
}

function FlyoutLink({
  label,
  active,
  disabled,
  onClick,
  testId,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      data-testid={testId}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        px: 1.5,
        py: 0.75,
        borderRadius: 2,
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        color: disabled
          ? "text.disabled"
          : active
            ? "text.primary"
            : "text.secondary",
        bgcolor: active ? "action.selected" : "transparent",
        cursor: disabled ? "default" : "pointer",
        "&:hover": { bgcolor: disabled ? "transparent" : "action.hover" },
      }}
    >
      {label}
    </Box>
  );
}
